import { m } from '@hub/messages';
import { Link, useGoBackOrHome, useMatches } from '@hub/router';
import type { BackBehavior } from '@hub/router/definePageRoute';
import { useFeatureFlag } from '@platform/feature-flags';
import {
  BackButton,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  SitePage,
} from '@platform/ui';
import { Fragment, type ReactNode } from 'react';
import { HomePaths } from '../../domains/home/route.paths';
import { BannerStack } from '../banners';
import { toBreadcrumbs } from './breadcrumbs';

interface PageProps {
  title: ReactNode;
  description?: ReactNode;
  backFallback?: string;
  backBehavior?: BackBehavior;
  hideBack?: boolean;
  children: ReactNode;
}

export function Page({ title, description, backFallback = HomePaths.home.path, backBehavior, hideBack, children }: PageProps) {
  const goBack = useGoBackOrHome(backFallback, { behavior: backBehavior });
  // `select` keeps this chrome out of every unrelated router commit: structural sharing preserves the
  // crumb array's identity, so the page re-renders when the trail changes and not when a loader does.
  const breadcrumbs = useMatches({ select: toBreadcrumbs, structuralSharing: true });
  const showBreadcrumbs = useFeatureFlag('platform-breadcrumbs');

  const hasNavigation = breadcrumbs.length > 0;
  const lastIndex = breadcrumbs.length - 1;
  const backLabel = m.commons_nav_back();

  const breadcrumbsNode =
    hasNavigation && showBreadcrumbs ? (
      <Breadcrumb aria-label={m.commons_nav_breadcrumbs()}>
        <BreadcrumbList>
          {breadcrumbs.map((crumb, index) => (
            <Fragment key={crumb.href}>
              <BreadcrumbItem>
                {index === lastIndex ? (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.href}>{crumb.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {index !== lastIndex ? <BreadcrumbSeparator /> : null}
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    ) : null;

  const backButtonNode =
    hasNavigation && !hideBack ? (
      <BackButton className="self-start" onClick={goBack} aria-label={backLabel}>
        {backLabel}
      </BackButton>
    ) : null;

  return (
    <SitePage title={title} description={description} breadcrumbs={breadcrumbsNode} backButton={backButtonNode}>
      <BannerStack scope="route" className="pb-3" />
      {children}
    </SitePage>
  );
}
